'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, SkeletonBar, StockStatusEnum, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useState } from 'react';
import type { StoreData } from '@libs/common-types';
import { validateStockStatus } from '../../utils/stockStatusUtils';
import { type UseMiniPdpBopisProps, useMiniPdpBopis } from './model/use-mini-pdp-bopis';
type MiniPdpBopisDetailsProps = UseMiniPdpBopisProps & Readonly<{
  onOpenStoreDrawer: () => void;
}>;
type MiniPdpBopisStoreStatusProps = Readonly<{
  isLoading: boolean;
  availableStock: number;
  minimumCaseQty: number;
  storeDetails?: StoreData['storeDetails'] | null;
}>;
function MiniPdpBopisStoreStatus(t0) {
  const $ = _c(30);
  const {
    isLoading,
    storeDetails,
    availableStock,
    minimumCaseQty
  } = t0;
  const [showTooltip, setShowTooltip] = useState(false);
  const storeName = storeDetails?.name;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setShowTooltip(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleShowTooltip = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setShowTooltip(false);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleHideTooltip = t2;
  if (isLoading) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <SkeletonBar className="mini-pdp-drawer__bopis-status-skeleton" />;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  if (!storeName) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <TextPassage size="lg">Select a store to check availability</TextPassage>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  let t3;
  if ($[4] !== availableStock || $[5] !== minimumCaseQty) {
    t3 = validateStockStatus({
      stockQuantity: availableStock,
      minimumCaseQty
    });
    $[4] = availableStock;
    $[5] = minimumCaseQty;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const stockStatus = t3;
  let stockText = "Out of stock";
  if (stockStatus === StockStatusEnum.InStock) {
    stockText = "In stock";
  }
  if (stockStatus === StockStatusEnum.FewLeftInStock) {
    stockText = "Only a few left";
  }
  const t4 = stockStatus === StockStatusEnum.InStock;
  const t5 = stockStatus !== StockStatusEnum.InStock;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = cn({
      "sparky-u-color-utility-success": t4,
      "sparky-u-color-utility-error": t5
    });
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="mini-pdp-drawer__bopis-store"> at </span>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = showTooltip ? "block" : "none";
  let t9;
  if ($[11] !== t8) {
    t9 = {
      display: t8
    };
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = storeDetails?.streetLine1;
  let t11;
  if ($[13] !== storeDetails) {
    t11 = storeDetails?.streetLine2 && <><br />{storeDetails.streetLine2}</>;
    $[13] = storeDetails;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <br />;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = storeDetails?.city;
  const t14 = storeDetails?.stateProvinceAbbreviation;
  const t15 = storeDetails?.zipPostalCode;
  let t16;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t13 || $[19] !== t14 || $[20] !== t15 || $[21] !== t9) {
    t16 = <span className="sparky-c-tooltip__content" style={t9}>{t10}{t11}{t12}{t13}, {t14} {t15}</span>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t9;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== storeName || $[24] !== t16) {
    t17 = <button className="sparky-u-theme-typography-body-lg-bold mini-pdp-drawer__bopis-store button-reset" data-testid="mini-pdp-bopis-store-name" type="button" onMouseEnter={handleShowTooltip} onMouseLeave={handleHideTooltip} onFocus={handleShowTooltip} onBlur={handleHideTooltip}>{storeName}{t16}</button>;
    $[23] = storeName;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== stockText || $[27] !== t17 || $[28] !== t6) {
    t18 = <div className="mini-pdp-drawer__bopis-store-status"><TextPassage size="lg" className={t6}>{stockText}{t7}{t17}</TextPassage></div>;
    $[26] = stockText;
    $[27] = t17;
    $[28] = t6;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  return t18;
}
export function MiniPdpBopisDetails(t0) {
  const $ = _c(20);
  const {
    product,
    tailored,
    isLoading,
    isError,
    onOpenStoreDrawer
  } = t0;
  let t1;
  if ($[0] !== isError || $[1] !== isLoading || $[2] !== product || $[3] !== tailored) {
    t1 = {
      product,
      tailored,
      isLoading,
      isError
    };
    $[0] = isError;
    $[1] = isLoading;
    $[2] = product;
    $[3] = tailored;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    shouldRender,
    storeDetails,
    availableStock,
    minimumCaseQty
  } = useMiniPdpBopis(t1);
  if (!shouldRender) {
    return null;
  }
  let t2;
  if ($[5] !== availableStock || $[6] !== isLoading || $[7] !== minimumCaseQty || $[8] !== storeDetails) {
    t2 = <MiniPdpBopisStoreStatus availableStock={availableStock} minimumCaseQty={minimumCaseQty} isLoading={isLoading} storeDetails={storeDetails} />;
    $[5] = availableStock;
    $[6] = isLoading;
    $[7] = minimumCaseQty;
    $[8] = storeDetails;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== isLoading || $[11] !== onOpenStoreDrawer || $[12] !== storeDetails) {
    t3 = <div className="delivery-details__change-store mini-pdp-drawer__bopis-change-store">{isLoading ? <SkeletonBar data-testid="mini-pdp-change-store-skeleton" className="delivery-details__change-store-skeleton mini-pdp-drawer__bopis-change-store-skeleton" /> : <Button type="button" variant="link" className="delivery-details__change-store-btn" text={storeDetails ? "Change store" : "Find a nearby store"} data-testid={storeDetails ? "mini-pdp-change-store" : "mini-pdp-find-nearby-store"} onClick={onOpenStoreDrawer} />}</div>;
    $[10] = isLoading;
    $[11] = onOpenStoreDrawer;
    $[12] = storeDetails;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== minimumCaseQty) {
    t4 = minimumCaseQty >= 2 && <TextPassage data-testid="mini-pdp-bopis-min-qty" className="delivery-details__minimum-case-qty mini-pdp-drawer__bopis-min-qty">Minimum quantity of {minimumCaseQty} required</TextPassage>;
    $[14] = minimumCaseQty;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t5 = <div className="mini-pdp-drawer__bopis-details" data-testid="mini-pdp-bopis-details">{t2}{t3}{t4}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}